import HealthScoreQuestionnaire from "@/features/HealthScore/components/HealthScoreQuestionnaire";
import {
  Question,
  QuestionnaireAnswer,
  QuestionType,
  QuestionnaireType,
  FamilyHistoryCondition,
  YesNoAnswer,
  ConditionsCount,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/types";
import useGetHealthScoreQuestionnaire, {
  healthScoreQuestionnaireQueryKey,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/hooks/useGetHealthScoreQuestionnaire";
import {
  submitHealthScoreQuestionnaire,
  SubmitHealthScoreQuestionnairePayload,
} from "@/services/core-api-adapter";
import DefaultError from "@/features/Common/components/DefaultError";
import { useQueryClient } from "@tanstack/react-query";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetHealthProfile from "../../hooks/useGetHealthProfile";
import { healthScoreSummaryQueryKey } from "../../hooks/useGetHealthScoreSummary";

export interface HealthHistoryQuestionnaireProps {
  onClose?: () => void;
}

export default function HealthHistoryQuestionnaire({
  onClose: onCloseProp,
}: HealthHistoryQuestionnaireProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchHealthProfile: refetch } = useGetHealthProfile();
  const [submitError, setSubmitError] = useState(false);

  const {
    questionnaireData: initialAnswers,
    isQuestionnaireLoading,
    questionnaireFetchError,
    refetchQuestionnaire,
  } = useGetHealthScoreQuestionnaire(QuestionnaireType.HEALTH_HISTORY);

  const HEALTH_HISTORY_QUESTIONS: Question[] = [
    {
      type: QuestionType.FAMILY_HISTORY,
      multiSelect: true,
      meta: {
        values: [
          FamilyHistoryCondition.DIABETES,
          FamilyHistoryCondition.HIGH_BLOOD_PRESSURE,
          FamilyHistoryCondition.CORONARY_ARTERY_DISEASE,
          FamilyHistoryCondition.NONE,
        ],
      },
    },
    {
      type: QuestionType.FAMILY_TUBERCULOSIS,
      meta: {
        values: [YesNoAnswer.YES, YesNoAnswer.NO],
      },
    },
    {
      type: QuestionType.CONDITIONS,
      meta: {
        values: [
          ConditionsCount.NONE,
          ConditionsCount.ONE,
          ConditionsCount.TWO_PLUS,
        ],
      },
    },
    {
      type: QuestionType.HIV_TB,
      meta: {
        values: [YesNoAnswer.YES, YesNoAnswer.NO],
      },
    },
  ];

  const handleSubmit = async (answers: QuestionnaireAnswer[]) => {
    const payload: SubmitHealthScoreQuestionnairePayload = {
      questionnaireType: QuestionnaireType.HEALTH_HISTORY,
      value: answers,
    };
    try {
      await submitHealthScoreQuestionnaire(payload);
      await queryClient.invalidateQueries({
        queryKey: healthScoreQuestionnaireQueryKey(
          QuestionnaireType.HEALTH_HISTORY
        ),
      });
      await refetch();
      await queryClient.invalidateQueries({
        queryKey: healthScoreSummaryQueryKey,
      });
      if (onCloseProp) onCloseProp();
      else navigate(-1);
    } catch {
      setSubmitError(true);
    }
  };

  const handleClose = () => {
    if (onCloseProp) onCloseProp();
    else navigate(-1);
  };

  const renderQuestionnaireError = (onTryAgain: () => void) => (
    <DefaultError onComeBackLater={handleClose} onTryAgain={onTryAgain}>
      <Typography variant="h2" pb={2}>
        {t("common.genericErrorMessage")}
      </Typography>
    </DefaultError>
  );

  if (isQuestionnaireLoading) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  if (questionnaireFetchError) {
    return renderQuestionnaireError(() => refetchQuestionnaire());
  }

  if (submitError) {
    return renderQuestionnaireError(() => setSubmitError(false));
  }

  return (
    <HealthScoreQuestionnaire
      questions={HEALTH_HISTORY_QUESTIONS}
      translationPrefix="HealthHistory.questionnaire"
      onSubmit={handleSubmit}
      onClose={handleClose}
      title={t("HealthHistory.questionnaire.title")}
      initialAnswers={initialAnswers ?? {}}
    />
  );
}
